import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Train, ArrowLeftRight, Search } from "lucide-react";
import { SWISS_CITIES } from "../lib/swissTrainData";
import { toolautosubmitEnabled } from "../lib/webmcpConfig";

export default function SearchPage() {
  const navigate = useNavigate();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];
  const today = new Date().toISOString().split("T")[0];

  const [originQuery, setOriginQuery] = useState("Zürich");
  const [destQuery, setDestQuery] = useState("Bern");
  const [showOriginList, setShowOriginList] = useState(false);
  const [showDestList, setShowDestList] = useState(false);
  const [tripType, setTripType] = useState("one-way");

  const originRef = useRef(null);
  const destRef = useRef(null);
  const outboundDateRef = useRef(null);
  const inboundDateRef = useRef(null);
  const passengersRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const originEl = originRef.current;
    const destEl = destRef.current;
    if (!originEl || !destEl) return;

    const onOriginInput = (e) => setOriginQuery(e.target.value);
    const onDestInput = (e) => setDestQuery(e.target.value);

    originEl.addEventListener("input", onOriginInput);
    destEl.addEventListener("input", onDestInput);

    return () => {
      originEl.removeEventListener("input", onOriginInput);
      destEl.removeEventListener("input", onDestInput);
    };
  }, []);

  const filteredOrigins = useMemo(
    () =>
      SWISS_CITIES.filter(
        (c) => c.toLowerCase().includes(originQuery.toLowerCase()) && c !== destQuery
      ).slice(0, 8),
    [originQuery, destQuery]
  );

  const filteredDests = useMemo(
    () =>
      SWISS_CITIES.filter(
        (c) => c.toLowerCase().includes(destQuery.toLowerCase()) && c !== originQuery
      ).slice(0, 8),
    [destQuery, originQuery]
  );

  const swapCities = () => {
    const originEl = originRef.current;
    const destEl = destRef.current;
    if (!originEl || !destEl) return;

    const tmp = originEl.value;
    originEl.value = destEl.value;
    destEl.value = tmp;
    setOriginQuery(originEl.value);
    setDestQuery(destEl.value);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const event = e.nativeEvent;
    const fd = new FormData(formRef.current);

    const origin = String(fd.get("origin") || "").trim();
    const destination = String(fd.get("destination") || "").trim();
    const outboundDate = String(fd.get("outboundDate") || "").trim();
    const inboundDate = String(fd.get("inboundDate") || "").trim();
    const passengers = String(fd.get("passengers") || "1").trim();

    if (!origin || !destination || !outboundDate) return;

    const params = new URLSearchParams({
      origin,
      destination,
      tripType,
      outboundDate,
      passengers,
    });

    if (tripType === "round-trip" && inboundDate) {
      params.set("inboundDate", inboundDate);
    }

    const resultsUrl = `/results?${params.toString()}`;

    if (typeof event.respondWith === "function" && event.agentInvoked) {
      event.respondWith({
        ok: true,
        resultsUrl,
      });
    }

    window.location.assign(resultsUrl);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-background">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-5">
          <Train className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">Swiss Train Search</h1>
        <p className="text-muted-foreground text-lg max-w-md mx-auto">
          Find the best train connections across Switzerland
        </p>
      </div>

      <form
        ref={formRef}
        onSubmit={handleSearch}
        className="w-full max-w-lg bg-card border border-border rounded-2xl p-7 shadow-lg space-y-5"
        toolname="searchTrains"
        tooldescription="Search for Swiss train connections between two cities. Returns a list of available trains with prices, departure/arrival times, and stops."
        {...(toolautosubmitEnabled ? { toolautosubmit: "" } : {})}
      >
        <div className="relative space-y-4">
          <div className="relative">
            <Label className="text-sm font-medium mb-1.5 block">Origin</Label>
            <input
              ref={originRef}
              placeholder="Search city…"
              defaultValue="Zürich"
              onChange={(e) => setOriginQuery(e.target.value)}
              onFocus={() => setShowOriginList(true)}
              onBlur={() => setTimeout(() => setShowOriginList(false), 150)}
              className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              name="origin"
              toolparamtitle="origin"
              toolparamdescription="Departure city in Switzerland (e.g. Zürich, Bern, Basel)"
            />
            {showOriginList && originQuery && filteredOrigins.length > 0 && (
              <div className="absolute z-20 top-full mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredOrigins.map((city) => (
                  <button
                    key={city}
                    type="button"
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-accent transition-colors"
                    onMouseDown={() => {
                      if (!originRef.current) return;
                      originRef.current.value = city;
                      setOriginQuery(city);
                      setShowOriginList(false);
                    }}
                  >
                    {city}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="absolute right-3 top-[52px] z-10">
            <button
              type="button"
              onClick={swapCities}
              className="p-1.5 rounded-full bg-muted hover:bg-accent border border-border transition-colors"
            >
              <ArrowLeftRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          <div className="relative">
            <Label className="text-sm font-medium mb-1.5 block">Destination</Label>
            <input
              ref={destRef}
              placeholder="Search city…"
              defaultValue="Bern"
              onChange={(e) => setDestQuery(e.target.value)}
              onFocus={() => setShowDestList(true)}
              onBlur={() => setTimeout(() => setShowDestList(false), 150)}
              className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              name="destination"
              toolparamtitle="destination"
              toolparamdescription="Arrival city in Switzerland (e.g. Lausanne, Lugano, Geneva)"
            />
            {showDestList && destQuery && filteredDests.length > 0 && (
              <div className="absolute z-20 top-full mt-1 w-full bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredDests.map((city) => (
                  <button
                    key={city}
                    type="button"
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-accent transition-colors"
                    onMouseDown={() => {
                      if (!destRef.current) return;
                      destRef.current.value = city;
                      setDestQuery(city);
                      setShowDestList(false);
                    }}
                  >
                    {city}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Outbound Date</Label>
            <input
              ref={outboundDateRef}
              type="date"
              min={today}
              defaultValue={tomorrowStr}
              className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              name="outboundDate"
              toolparamdescription="Outbound travel date in YYYY-MM-DD format"
            />
          </div>
          {tripType === "round-trip" && (
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Return Date</Label>
              <input
                ref={inboundDateRef}
                type="date"
                min={today}
                className="flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                name="inboundDate"
                toolparamdescription="Return travel date in YYYY-MM-DD format (required for round-trip)"
              />
            </div>
          )}
        </div>

        <div>
          <Label className="text-sm font-medium mb-1.5 block">Number of Passengers</Label>
          <input
            ref={passengersRef}
            type="number"
            min={1}
            max={9}
            defaultValue={1}
            className="flex h-11 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            name="passengers"
            toolparamdescription="Number of passengers travelling (1–9)"
          />
        </div>

        <Button type="submit" size="lg" className="w-full h-12 text-base font-semibold">
          <Search className="w-5 h-5 mr-2" />
          Search Trains
        </Button>
      </form>
    </div>
  );
}